import { Box } from '@mui/material';
import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import { SearchableSelect } from 'components/inputs/select/searchableSelect';
import { SelectItem } from 'components/inputs/select/select';

import { Meridiem } from 'types/time';

import { TIMEZONE_OPTIONS, formatTimezoneSelectItem } from './keys';
import { StyledFormField, StyledTimeInput, StyledToggleButton, StyledToggleButtonGroup, TimeColon } from './styles';

export interface OffsetTime {
  hour: number;
  minute: number;
  period: Meridiem;
  offset: string;
}

interface Props {
  initialValue?: OffsetTime;
  onChange: (time: OffsetTime | null) => void;
  error?: string;
}

export const TimePicker = ({ initialValue, onChange, error }: Props) => {
  const now = new Date();
  const amPmHour = now.getHours() % 12 || 12;

  const initialValues: OffsetTime = {
    hour: initialValue?.hour ?? amPmHour,
    minute: initialValue?.minute ?? now.getMinutes(),
    period: initialValue?.period ?? (now.getHours() >= 12 ? Meridiem.PM : Meridiem.AM),
    offset: initialValue?.offset ?? formatTimezoneSelectItem(now.getTimezoneOffset() / -60).key,
  };

  const [hour, setHour] = useState<number>(initialValues.hour);
  const [minute, setMinute] = useState<number>(initialValues.minute);
  const [period, setPeriod] = useState<Meridiem>(initialValues.period);
  const [offset, setOffset] = useState<string>(initialValues.offset);

  const onHourChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      onChange(null);
      return;
    }

    setHour(parseInt(e.target.value, 10));
  };

  const onMinuteChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.value) {
      onChange(null);
      return;
    }
    setMinute(parseInt(e.target.value, 10));
  };

  const getBoundedInputHandler = (max: number) => (e: FormEvent<HTMLInputElement> & InputEvent) => {
    const input = e.target as HTMLInputElement;
    const next = input.value + (e as InputEvent).data;
    const num = parseInt(next, 10);
    if (Number.isNaN(num) || num > max) e.preventDefault();
  };

  const onMeridiemChange = (meridiem: Meridiem) => {
    setPeriod(meridiem);
  };

  const onTimezoneChange = (item: SelectItem | SelectItem[] | null) => {
    const selected = Array.isArray(item) ? item[0] : item;
    if (!selected?.label) {
      return;
    }

    setOffset(selected.label);
  };

  const getTimezoneSelectItem = (offset: string) =>
    TIMEZONE_OPTIONS.find((item) => item.label === offset || item.label === `UTC${offset}`) || TIMEZONE_OPTIONS[0];

  useEffect(() => {
    onChange({ hour, minute, period, offset });
  }, [hour, minute, period, offset]);

  return (
    <StyledFormField error={error}>
      <Box display="flex" alignItems="center" gap={1}>
        <StyledTimeInput
          type="number"
          inputProps={{ min: 1, max: 12 }}
          defaultValue={String(initialValues.hour).padStart(2, '0')}
          onChange={onHourChange}
          onBeforeInput={getBoundedInputHandler(12)}
          error={Boolean(error)}
        />
        <TimeColon>:</TimeColon>
        <StyledTimeInput
          type="number"
          defaultValue={String(initialValues.minute).padStart(2, '0')}
          onChange={onMinuteChange}
          inputProps={{ min: 0, max: 59 }}
          onBeforeInput={getBoundedInputHandler(59)}
          error={Boolean(error)}
        />
        <StyledToggleButtonGroup
          value={period}
          exclusive
          onChange={(_, val) => {
            if (!val) {
              return;
            }
            onMeridiemChange(val);
          }}
        >
          <StyledToggleButton value={Meridiem.AM}>{Meridiem.AM}</StyledToggleButton>
          <StyledToggleButton value={Meridiem.PM}>{Meridiem.PM}</StyledToggleButton>
        </StyledToggleButtonGroup>
      </Box>
      <SearchableSelect<SelectItem, false>
        isOriginalOrder
        fullWidth
        value={getTimezoneSelectItem(offset)}
        items={TIMEZONE_OPTIONS}
        onChange={onTimezoneChange}
        getItemLabel={(item) => item?.label ?? item?.key ?? ''}
        multiple={false}
        error={Boolean(error)}
        closeDropdownOnSelect
      />
    </StyledFormField>
  );
};
